import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import {
  HISTORICAL_ORTHOPHOTO_CONTROL_LABEL,
  HISTORICAL_ORTHOPHOTO_CURRENT_ID,
  HistoricalOrthophotoOption,
  getHistoricalOrthophotoOptions,
} from "src/managers/HistoricalOrthophoto";
import {
  MapitState,
  MapitStateContext,
  actionSelectBackgroundLayer,
  actionSetFeatureLayerVisibility,
  actionSetHistoricalOrthophotoControl,
} from "src/states/MapitState";
import { GlassCheckbox } from "./MitGlassComponents";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

function getSelectedBackgroundLayerKey(mapitState: MapitState) {
  if (mapitState.mapInfo.mapStyle.name === "Blank") {
    return "BlankMap";
  }
  if (
    mapitState.mapInfo.mapStyle.mods?.includes("OrthophotoNoWaterMask")
    || mapitState.mapInfo.mapStyle.mods?.includes("Ortophoto")
  ) {
    return "Orthophoto";
  }
  return "Map";
}

function useHistoricalOrthophotoController() {
  const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
  const cleanupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const options = useMemo(() => {
    return getHistoricalOrthophotoOptions(mapitState.featureLayers);
  }, [mapitState.featureLayers]);

  const optionMap = useMemo(() => {
    return new Map(options.map((option) => [option.id, option]));
  }, [options]);

  const selectedHistoricalOption = useMemo(() => {
    return options.find((option) => option.layerKey && mapitState.selectedFeatureLayerKeys.includes(option.layerKey));
  }, [options, mapitState.selectedFeatureLayerKeys]);

  const isEnabled = mapitState.historicalOrthophotoControl.enabled || Boolean(selectedHistoricalOption);
  const selectedOptionId = selectedHistoricalOption?.id
    || (optionMap.has(mapitState.historicalOrthophotoControl.selectedOptionId) ? mapitState.historicalOrthophotoControl.selectedOptionId : HISTORICAL_ORTHOPHOTO_CURRENT_ID);

  useEffect(() => {
    return () => {
      if (cleanupTimerRef.current) {
        clearTimeout(cleanupTimerRef.current);
      }
    };
  }, []);

  const clearHistoricalLayers = useCallback((keepOptionId?: string) => {
    const activeLayers = options
      .filter((option) => option.layerKey)
      .filter((option) => option.id !== keepOptionId)
      .map((option) => ({
        key: option.layerKey!,
        layer: mapitState.featureLayers[option.layerKey!],
      }));

    if (!activeLayers.length) {
      return;
    }

    mapitStateDispatch(actionSetFeatureLayerVisibility(HISTORICAL_ORTHOPHOTO_CONTROL_LABEL, false, activeLayers, false));
  }, [mapitState.featureLayers, mapitStateDispatch, options]);

  const scheduleHistoricalLayerCleanup = useCallback((keepOptionId?: string) => {
    if (cleanupTimerRef.current) {
      clearTimeout(cleanupTimerRef.current);
    }

    cleanupTimerRef.current = setTimeout(() => {
      clearHistoricalLayers(keepOptionId);
      cleanupTimerRef.current = null;
    }, 180);
  }, [clearHistoricalLayers]);

  const selectOption = useCallback((optionId: string, previousBackgroundLayerKey = mapitState.historicalOrthophotoControl.previousBackgroundLayerKey) => {
    if (optionId === HISTORICAL_ORTHOPHOTO_CURRENT_ID) {
      mapitStateDispatch(actionSelectBackgroundLayer("Orthophoto"));
      scheduleHistoricalLayerCleanup();
    } else {
      const option = optionMap.get(optionId);
      if (!option?.layerKey) {
        return;
      }

      mapitStateDispatch(actionSetFeatureLayerVisibility(option.label, true, [{
        key: option.layerKey,
        layer: mapitState.featureLayers[option.layerKey],
      }], false));
      scheduleHistoricalLayerCleanup(optionId);
    }

    mapitStateDispatch(actionSetHistoricalOrthophotoControl({
      enabled: true,
      selectedOptionId: optionId,
      previousBackgroundLayerKey,
    }));
  }, [mapitState.featureLayers, mapitState.historicalOrthophotoControl.previousBackgroundLayerKey, mapitStateDispatch, optionMap, scheduleHistoricalLayerCleanup]);

  const enable = useCallback(() => {
    const previousBackgroundLayerKey = getSelectedBackgroundLayerKey(mapitState);
    const nextOptionId = optionMap.has(selectedOptionId) ? selectedOptionId : HISTORICAL_ORTHOPHOTO_CURRENT_ID;
    selectOption(nextOptionId, previousBackgroundLayerKey);
  }, [mapitState, optionMap, selectOption, selectedOptionId]);

  const disable = useCallback(() => {
    if (cleanupTimerRef.current) {
      clearTimeout(cleanupTimerRef.current);
      cleanupTimerRef.current = null;
    }
    clearHistoricalLayers();
    mapitStateDispatch(actionSelectBackgroundLayer(mapitState.historicalOrthophotoControl.previousBackgroundLayerKey || "Map"));
    mapitStateDispatch(actionSetHistoricalOrthophotoControl({
      enabled: false,
      selectedOptionId: HISTORICAL_ORTHOPHOTO_CURRENT_ID,
      previousBackgroundLayerKey: mapitState.historicalOrthophotoControl.previousBackgroundLayerKey || "Map",
    }));
  }, [clearHistoricalLayers, mapitState.historicalOrthophotoControl.previousBackgroundLayerKey, mapitStateDispatch]);

  return {
    isEnabled,
    options,
    selectedOptionId,
    enable,
    disable,
    selectOption,
  };
}

export function HistoricalOrthophotoGroupToggle() {
  const { isEnabled, options, enable, disable } = useHistoricalOrthophotoController();

  if (options.length <= 1) {
    return null;
  }

  return (
    <div className="layer HistoricalOrthophotoGroupToggle">
      <GlassCheckbox onClick={(_evt, checked) => checked ? enable() : disable()} checked={isEnabled}>
        {HISTORICAL_ORTHOPHOTO_CONTROL_LABEL}
      </GlassCheckbox>
    </div>
  );
}

function HistoricalOrthophotoSliderMark(props: {
  option: HistoricalOrthophotoOption,
  isActive: boolean,
  onSelect: (optionId: string) => void,
}) {
  return (
    <button
      type="button"
      className={"HistoricalOrthophotoSlider-Mark" + (props.isActive ? " active" : "")}
      onClick={() => props.onSelect(props.option.id)}
    >
      <span className="HistoricalOrthophotoSlider-MarkDot" />
      <span className="HistoricalOrthophotoSlider-MarkLabel">{props.option.shortLabel}</span>
    </button>
  );
}

export function HistoricalOrthophotoMapSlider() {
  const { isEnabled, options, selectedOptionId, selectOption, disable } = useHistoricalOrthophotoController();

  const selectedIndex = Math.max(options.findIndex((option) => option.id === selectedOptionId), 0);
  const progress = options.length > 1 ? (selectedIndex / (options.length - 1)) * 100 : 0;
  const marksGridStyle = { gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` };

  if (!isEnabled || options.length <= 1) {
    return null;
  }

  return (
    <div className="HistoricalOrthophotoSlider">
      <div className="HistoricalOrthophotoSlider-Header">
        <span className="HistoricalOrthophotoSlider-Title">{HISTORICAL_ORTHOPHOTO_CONTROL_LABEL}</span>
        <button type="button" className="HistoricalOrthophotoSlider-Close" onClick={() => disable()} title="Close historical orthophoto slider">
          <MIBIcon icon="dismiss" />
        </button>
      </div>
      <div className="HistoricalOrthophotoSlider-TrackArea">
        <div className="HistoricalOrthophotoSlider-Marks HistoricalOrthophotoSlider-MarksLabels" style={marksGridStyle}>
          {options.map((option) => (
            <span key={option.id} className={"HistoricalOrthophotoSlider-MarkText" + (option.id === selectedOptionId ? " active" : "")}>
              {option.shortLabel}
            </span>
          ))}
        </div>
        <input
          className="slider HistoricalOrthophotoSlider-Input"
          type="range"
          min={0}
          max={Math.max(options.length - 1, 0)}
          step={1}
          value={selectedIndex}
          style={{ backgroundSize: `${progress}% 100%` }}
          onChange={(event) => {
            const nextOption = options[Number(event.target.value)];
            if (nextOption) {
              selectOption(nextOption.id);
            }
          }}
        />
        <div className="HistoricalOrthophotoSlider-Marks" style={marksGridStyle}>
          {options.map((option) => (
            <HistoricalOrthophotoSliderMark
              key={option.id}
              option={option}
              isActive={option.id === selectedOptionId}
              onSelect={selectOption}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
